/**
 * Phase 14.4 — Service page visual storytelling content registry.
 *
 * Per-service content for the <ServiceFlowDiagram>, <ServiceProcessTimeline>,
 * and <ScenarioSnapshot> modules. Compliance-safe: no "guaranteed", no
 * "top-rated", no fabricated numbers. Illustrative-only scenarios; charts
 * use rounded, obviously-illustrative numbers and carry an NAIC disclaimer.
 */

/** Short promise sentence, rendered as gold italic subtitle under the H1. */
export const SERVICE_PROMISE = {
  "life-insurance": "Protecting the people behind your plan — not just the paperwork.",
  "annuities": "Turning a retirement balance into income you can actually plan around.",
  "health-insurance": "Health coverage that matches the way you actually live and work.",
  "medicare": "Clarity on Medicare before birthdays force a decision.",
  "long-term-care": "A plan for the care you'd rather never need — and the family who shouldn't absorb it.",
  "disability-income": "If your paycheck stops, your plan shouldn't.",
  "business-insurance": "Keeping a business whole when the people behind it change.",
  "college-planning": "Funding college without raiding retirement.",
  "estate-planning": "A clean handoff for the people and causes you've built for.",
  "wealth-strategy": "One plan across every stage of life.",
};

/** 3-node money/benefit flow for the <ServiceFlowDiagram>. */
export const SERVICE_FLOW = {
  "life-insurance": {
    caption: "How life insurance converts a monthly premium into a tax-free legacy.",
    nodes: [
      { label: "You pay premium", sub: "Monthly or annual", icon: "coin" },
      { label: "Policy stays in force", sub: "Term or permanent", icon: "shield" },
      { label: "Family receives benefit", sub: "Generally income-tax-free", icon: "heart" },
    ],
  },
  "annuities": {
    caption: "How premium today becomes income that can last your lifetime.",
    nodes: [
      { label: "You contribute", sub: "Lump sum or over time", icon: "coin" },
      { label: "Value accumulates", sub: "Tax-deferred growth", icon: "trending" },
      { label: "Income flows for life", sub: "Elective, scheduled payouts", icon: "flow" },
    ],
  },
  "health-insurance": {
    caption: "How a plan matches a monthly premium to the care you actually use.",
    nodes: [
      { label: "You choose a plan", sub: "Marketplace, group, or HSA", icon: "note" },
      { label: "Premium + deductible", sub: "Monthly and per-event", icon: "coin" },
      { label: "Coverage activates", sub: "Preventive and catastrophic", icon: "shield" },
    ],
  },
  "medicare": {
    caption: "Medicare planning fits the right parts to the right year of life.",
    nodes: [
      { label: "Review eligibility", sub: "Age 65 window", icon: "note" },
      { label: "Pick A, B, D, and gap", sub: "Original + supplement or Advantage", icon: "layers" },
      { label: "Enroll + review yearly", sub: "Open-enrollment windows", icon: "shield" },
    ],
  },
  "long-term-care": {
    caption: "LTC turns premium today into coverage for extended care costs later.",
    nodes: [
      { label: "Premium today", sub: "Standalone or hybrid", icon: "coin" },
      { label: "Care needs arise", sub: "Home, assisted, facility", icon: "shield" },
      { label: "Policy pays benefits", sub: "Reimburses or pays directly", icon: "heart" },
    ],
  },
  "disability-income": {
    caption: "DI keeps a paycheck flowing when your ability to earn stops.",
    nodes: [
      { label: "You insure your income", sub: "% of earnings", icon: "coin" },
      { label: "Illness or injury", sub: "Can't work for a period", icon: "shield" },
      { label: "Monthly benefit pays you", sub: "Through elimination period", icon: "flow" },
    ],
  },
  "business-insurance": {
    caption: "Business insurance keeps revenue flowing when a key person is gone.",
    nodes: [
      { label: "Business buys coverage", sub: "Key-person, buy-sell, 162", icon: "note" },
      { label: "Triggering event", sub: "Death, disability, buyout", icon: "shield" },
      { label: "Proceeds fund continuity", sub: "Buyout, hiring, obligations", icon: "flow" },
    ],
  },
  "college-planning": {
    caption: "Steady deposits now compound into tuition dollars when you need them.",
    nodes: [
      { label: "You contribute monthly", sub: "529 or alternative", icon: "coin" },
      { label: "Market growth, tax-advantaged", sub: "Investment accounts grow", icon: "trending" },
      { label: "Funds pay qualified costs", sub: "Tuition, housing, books", icon: "heart" },
    ],
  },
  "estate-planning": {
    caption: "Estate tools coordinate who gets what, when, and how — with minimum friction.",
    nodes: [
      { label: "Documents and trusts", sub: "Will, POA, ILIT, revocable trust", icon: "note" },
      { label: "Life event", sub: "Incapacity, death, transfer", icon: "shield" },
      { label: "Assets move on plan", sub: "To heirs, causes, trusts", icon: "heart" },
    ],
  },
  "wealth-strategy": {
    caption: "Four coordinated layers — protect, accumulate, distribute, transfer.",
    nodes: [
      { label: "Protect", sub: "Insurance against downside", icon: "shield" },
      { label: "Accumulate", sub: "Compound growth", icon: "trending" },
      { label: "Distribute", sub: "Reliable income", icon: "flow" },
      { label: "Transfer", sub: "Legacy and causes", icon: "heart" },
    ],
  },
};

/** 5-step service-specific process. Each step: { label, note }. */
export const SERVICE_PROCESS = {
  "life-insurance": [
    { label: "Educate", note: "Walk through term vs permanent and how each actually works." },
    { label: "Assess need", note: "Income replacement, mortgage, education, final expenses." },
    { label: "Choose product", note: "Match product type and term to the need and budget." },
    { label: "Underwrite", note: "Application, exam if required, carrier decision." },
    { label: "Protect", note: "Coverage in force. Annual review as life changes." },
  ],
  "annuities": [
    { label: "Discover goals", note: "Target retirement income and risk tolerance." },
    { label: "Match product", note: "Fixed, indexed, or variable — pick the right chassis." },
    { label: "Fund", note: "Lump sum, rollover, or scheduled contributions." },
    { label: "Review annually", note: "Confirm riders and performance against plan." },
    { label: "Activate income", note: "Elect lifetime income when retirement begins." },
  ],
  "health-insurance": [
    { label: "Assess need", note: "Household, employer offer, and expected utilization." },
    { label: "Compare plans", note: "Marketplace, group, HSA — side by side." },
    { label: "Enroll", note: "During open enrollment or qualifying event." },
    { label: "Use coverage", note: "Preventive first; understand your deductible." },
    { label: "Re-shop yearly", note: "Confirm network + premium match current life." },
  ],
  "medicare": [
    { label: "Eligibility review", note: "Confirm Part A/B window and employer coverage." },
    { label: "Learn parts", note: "A, B, D, Advantage, Supplement — what each covers." },
    { label: "Choose structure", note: "Original + Supplement or Advantage + Part D." },
    { label: "Enroll", note: "Initial enrollment or annual election period." },
    { label: "Annual check", note: "Review drugs, network, and premium every year." },
  ],
  "long-term-care": [
    { label: "Risk discussion", note: "Family history, location cost, and timeline." },
    { label: "Choose benefit period", note: "Daily / monthly benefit, elimination period." },
    { label: "Health review", note: "Simplified or full underwriting." },
    { label: "Lock in coverage", note: "Standalone, hybrid, or rider-based." },
    { label: "Annual check", note: "Confirm inflation rider and family contacts." },
  ],
  "disability-income": [
    { label: "Define income to protect", note: "Base, bonus, and benefit replacement goal." },
    { label: "Choose structure", note: "Short- or long-term, own-occupation definition." },
    { label: "Underwrite", note: "Financial and medical underwriting." },
    { label: "Policy in force", note: "Coverage protects your paycheck." },
    { label: "Review", note: "Increase coverage as income grows." },
  ],
  "business-insurance": [
    { label: "Map risks", note: "Owners, key employees, debt obligations." },
    { label: "Structure agreements", note: "Buy-sell, key person, 162 executive bonus." },
    { label: "Fund the plan", note: "Life, disability, or hybrid policies." },
    { label: "Document", note: "Coordinate with attorney and CPA." },
    { label: "Review", note: "Annual check against valuation and ownership changes." },
  ],
  "college-planning": [
    { label: "Set the target", note: "Projected cost + family contribution." },
    { label: "Choose the vehicle", note: "529, UTMA, Roth, or cash-value life." },
    { label: "Automate", note: "Monthly contribution + beneficiary setup." },
    { label: "Invest thoughtfully", note: "Age-based glide path or custom allocation." },
    { label: "Distribute", note: "Qualified withdrawals for tuition and costs." },
  ],
  "estate-planning": [
    { label: "Inventory", note: "Assets, beneficiaries, and goals." },
    { label: "Document", note: "Will, POA, trust — with your attorney." },
    { label: "Fund the trust", note: "Title accounts and property correctly." },
    { label: "Layer insurance", note: "ILIT, survivorship, or LTC where needed." },
    { label: "Review every 3–5 yrs", note: "Life changes trigger updates." },
  ],
  "wealth-strategy": [
    { label: "Discover", note: "Map current protection, savings, income." },
    { label: "Diagnose gaps", note: "Income replacement, retirement, legacy." },
    { label: "Design layers", note: "Protect / Accumulate / Distribute / Transfer." },
    { label: "Implement", note: "Sequence the moves that matter most." },
    { label: "Review", note: "Rebalance every life stage." },
  ],
};

/**
 * Illustrative scenario. Chart kind drives which Recharts module renders.
 *   - gap:      bar — needed vs typical employer coverage
 *   - income:   area — annuity income stream over 25 years
 *   - careCost: line — projected care cost over age timeline
 *   - cashvalue: line — IUL cash value vs taxable alt (life-insurance alt)
 *   - college529: area — 529 projection
 *   - incomeReplace: bar — income replacement by age (DI)
 *   - keymanLoss:   bar — revenue impact of key-person loss (business)
 *   - estateTransfer: bar — asset transfer with and without ILIT
 *   - wealthCurve: line — accumulation/distribution curve
 */
export const SERVICE_SCENARIO = {
  "life-insurance": {
    situation: "Mark, 38, two kids, 20 years left on a mortgage. Employer coverage ≈ $100k.",
    approach: "A 20-year, $750k term policy covers mortgage + 10 years of income replacement.",
    outcome: "Family keeps the house and lifestyle if Mark passes during the mortgage years.",
    chartKind: "gap",
    chartTitle: "Coverage gap: employer alone vs needed",
  },
  "annuities": {
    situation: "Linda and David, 58 and 60, want a reliable retirement paycheck starting at 65.",
    approach: "A fixed indexed annuity with a lifetime income rider funds a floor under Social Security.",
    outcome: "Predictable monthly income for life, regardless of market behavior.",
    chartKind: "income",
    chartTitle: "Illustrative annuity income (ages 65–90)",
  },
  "health-insurance": {
    situation: "Sara, 34, self-employed, healthy, expects routine preventive care.",
    approach: "A bronze marketplace plan paired with an HSA keeps premium low and pre-tax savings growing.",
    outcome: "Lower monthly cost + tax-advantaged medical savings for future needs.",
    chartKind: "gap",
    chartTitle: "Illustrative premium comparison",
  },
  "medicare": {
    situation: "Robert, 64, retiring at 65, with a Medigap-eligible state.",
    approach: "Original Medicare + a Plan G supplement + a standalone Part D.",
    outcome: "Predictable monthly cost, broad provider choice, and clear drug coverage.",
    chartKind: "careCost",
    chartTitle: "Illustrative annual out-of-pocket by option",
  },
  "long-term-care": {
    situation: "Patricia, 55, mother needed 3 years of memory care. Patricia wants to avoid repeating the pattern.",
    approach: "A hybrid LTC-life policy with a 4-year benefit and inflation rider.",
    outcome: "Protects retirement assets and clarifies responsibilities for adult children.",
    chartKind: "careCost",
    chartTitle: "Illustrative annual care cost by age",
  },
  "disability-income": {
    situation: "Dr. Chen, 32, $220k income, student loans, newly in private practice.",
    approach: "Own-occupation long-term disability covering ~60% of income to age 65.",
    outcome: "Income continues if Dr. Chen can't practice their specialty due to illness or injury.",
    chartKind: "incomeReplace",
    chartTitle: "Illustrative paycheck without / with DI",
  },
  "business-insurance": {
    situation: "Two owners of a 10-person services firm, roughly equal equity.",
    approach: "A cross-purchase buy-sell funded with term life, plus key-person coverage on each owner.",
    outcome: "Surviving owner can buy out the family without draining the business.",
    chartKind: "keymanLoss",
    chartTitle: "Illustrative revenue impact after a key-person loss",
  },
  "college-planning": {
    situation: "The Shahs, parents of a 3-year-old, targeting an in-state 4-year school.",
    approach: "Monthly 529 contribution with age-based glide path, modest UTMA for non-qualified needs.",
    outcome: "Projected funding covers roughly 70–85% of in-state cost at age 18.",
    chartKind: "college529",
    chartTitle: "Illustrative 529 balance over 15 years",
  },
  "estate-planning": {
    situation: "Jim and Sue, mid-60s, $4.5M estate, mix of real estate and retirement accounts.",
    approach: "Revocable trust + ILIT holding a survivorship policy to cover estate-tax liquidity.",
    outcome: "Heirs receive assets efficiently; no forced sale of real estate for liquidity.",
    chartKind: "estateTransfer",
    chartTitle: "Illustrative transfer efficiency",
  },
  "wealth-strategy": {
    situation: "Across clients: coverage gap at 35, savings acceleration at 45, income planning at 60.",
    approach: "Coordinated 4-layer plan sequenced by life stage, reviewed every 3–5 years.",
    outcome: "Each layer supports the next — protection enables accumulation, accumulation enables distribution, distribution enables transfer.",
    chartKind: "wealthCurve",
    chartTitle: "Illustrative lifetime wealth curve",
  },
};

/** Persona label + fit line (persona slugs come from the service object). */
export const PERSONA_FIT = {
  "young-families": { name: "Young families", fit: "Growing household, income to protect, mortgage in play." },
  "doctors-high-earners": { name: "Doctors & high earners", fit: "Big future income, early-career liability, student loans." },
  "w2-executives": { name: "W-2 executives", fit: "Compensation includes bonus and equity; coverage gaps at group level." },
  "pre-retirees": { name: "Pre-retirees (55+)", fit: "Shifting from accumulation to reliable income." },
  "retirees": { name: "Retirees", fit: "Protecting lifestyle and legacy; longevity is the real risk." },
  "small-business-owners": { name: "Small business owners", fit: "Personal guarantees, buy-sell, and key-person exposure." },
  "high-net-worth": { name: "High-net-worth families", fit: "Estate-level planning, liquidity, intergenerational transfer." },
  "parents": { name: "Parents of young kids", fit: "College funding without derailing retirement." },
  "medicare-eligible": { name: "Medicare-eligible adults", fit: "Choosing the right mix of Medicare parts for the next decade." },
  "sandwich-generation": { name: "Sandwich generation", fit: "Caring for parents while supporting kids." },
};
